/**
 * Widget App Component
 *
 * Thin composition layer. Data loading lives in `useMarketEventData`, view
 * transitions in `useViewState`. This component only maps state → components.
 *
 * The initial surface is set by `data-default-view` (config `defaultView`,
 * fallback preview). Surfaces correspond to Figma states; transitions:
 *   pill     → preview  via pill click
 *   preview  → pill     via X in preview
 *   preview  → card     via expand arrow
 *   card     → preview  via card close
 *
 * While loading, the inline position shows `WidgetSkeleton` in the reserved
 * slot; floating positions render nothing until a market resolves.
 */

import type { ComponentChildren, JSX } from "preact";
import { useEffect, useLayoutEffect, useRef, useState } from "preact/hooks";
import { WidgetState, WidgetView, type WidgetAppProps } from "../types";
import { getConfig } from "../config";
import { useMarketEventData, useViewState } from "../hooks";
import { inlineTrailingSpaceClass, WidgetPosition } from "../utils/constants";
import { MarketCard } from "./market-card";
import { MarketPill } from "./market-pill";
import { MarketPreview } from "./market-preview";
import { WidgetSkeleton } from "./widget-skeleton";

export function WidgetApp({
  apiKey,
  tenantId,
  pageUrl,
  onSettled,
}: WidgetAppProps): JSX.Element | null {
  const { state, marketEvent } = useMarketEventData({
    apiKey,
    tenantId,
    pageUrl,
  });
  const { view, openPill, openPreview, openCard, reset } = useViewState(
    getConfig().defaultView,
  );

  // Reset the surface when the inputs that drive a new market lookup change.
  // Without this, a widget left in "pill" or "card" by the user would render
  // the next event in the stale surface instead of the configured default.
  useEffect(() => {
    reset();
  }, [apiKey, tenantId, pageUrl, reset]);

  // Signal once the load resolves (ready/empty/error). The inline host uses
  // this to drop its space reservation — empty/error render null, so the
  // reserved slot must collapse rather than leave a permanent gap.
  useEffect(() => {
    if (state !== WidgetState.Loading) {
      onSettled?.();
    }
  }, [state, onSettled]);

  // Loading placeholder is inline-only: floating positions overlay the page
  // and reserve no space, so a skeleton there would just be a stray pill.
  if (state === WidgetState.Loading) {
    return getConfig().position === WidgetPosition.Inline ? (
      <WidgetSkeleton />
    ) : null;
  }

  if (state !== WidgetState.Ready || !marketEvent) {
    return null;
  }

  const surfaces = (
    <>
      <MarketPill onClick={openPreview} visible={view === WidgetView.Pill} />
      <MarketPreview
        marketEvent={marketEvent}
        onDismiss={openPill}
        onExpand={openCard}
        visible={view === WidgetView.Preview}
      />
      <MarketCard
        marketEvent={marketEvent}
        onClose={openPreview}
        visible={view === WidgetView.Card}
      />
    </>
  );

  // Floating surfaces are `position: fixed` (out of flow), so a state switch
  // never reflows the page — render them bare. Inline surfaces live in document
  // flow, so the height-animating stage prevents the article from jumping while
  // the outgoing surface fades out (see `InlineStage`).
  return getConfig().position === WidgetPosition.Inline ? (
    <InlineStage view={view}>{surfaces}</InlineStage>
  ) : (
    surfaces
  );
}

/**
 * Inline-only transition stage. The outgoing surface stays mounted for its exit
 * animation, so two surfaces are briefly in flow at once; left alone they stack
 * and jump the partner article. The exiting preview/card surfaces position
 * themselves `absolute` (see their inline-exit branch), so only the active
 * surface dictates flow — this stage measures that surface and animates its own
 * `height` between states, giving a smooth cross-fade-in-place instead of a
 * stack-and-snap. Height animation is disabled under `prefers-reduced-motion`.
 */
function InlineStage({
  view,
  children,
}: {
  view: WidgetView;
  children: ComponentChildren;
}): JSX.Element {
  const stageRef = useRef<HTMLDivElement>(null);
  const [height, setHeight] = useState<number>();

  useLayoutEffect((): (() => void) | undefined => {
    const stage = stageRef.current;
    if (!stage) return undefined;

    // The active surface is the only in-flow child; exiting surfaces are
    // absolutely positioned, so they overlay without dictating height.
    const measure = (): void => {
      const active = Array.from(stage.children).find(
        (child) => getComputedStyle(child).position !== "absolute",
      );
      if (active instanceof HTMLElement) setHeight(active.offsetHeight);
    };

    measure();

    // jsdom has no ResizeObserver; the one-shot measure above is enough there.
    if (typeof ResizeObserver === "undefined") return undefined;
    const observer = new ResizeObserver(measure);
    for (const child of Array.from(stage.children)) observer.observe(child);
    return () => observer.disconnect();
  }, [view]);

  return (
    <div
      className={`pw:relative pw:transition-[height] pw:duration-300 pw:ease-out pw:motion-reduce:transition-none ${inlineTrailingSpaceClass}`}
      ref={stageRef}
      style={height === undefined ? undefined : { height: `${height}px` }}
    >
      {children}
    </div>
  );
}
